import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useApp } from './AppContext';
import { hexToHslString, isLightColor } from '@/lib/color-utils';
import type { BrandProfile } from '@/lib/brand-types';

export interface BrandContextValue {
  companyName: string;
  logoUrl: string | null;
  poweredByWt: boolean;
  isBranded: boolean;
  brandProfile: BrandProfile | null;
}

const BrandContext = createContext<BrandContextValue>({
  companyName: 'Watt Tower',
  logoUrl: null,
  poweredByWt: true,
  isBranded: false,
  brandProfile: null,
});

/** CSS variables overridden by brand accent color (accent-only strategy). */
const ACCENT_VARS = [
  '--primary',
  '--sidebar-primary',
  '--ring',
  '--sidebar-ring',
] as const;

const FOREGROUND_VARS = [
  '--primary-foreground',
  '--sidebar-primary-foreground',
] as const;

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const { organization } = useApp();
  const bp = (organization?.brand_profile as BrandProfile | null) ?? null;

  const value = useMemo<BrandContextValue>(() => {
    if (!bp) {
      return {
        companyName: organization?.name ?? 'Watt Tower',
        logoUrl: null,
        poweredByWt: true,
        isBranded: false,
        brandProfile: null,
      };
    }
    return {
      companyName: bp.company_name || organization?.name || 'Watt Tower',
      logoUrl: bp.logo_url ?? null,
      poweredByWt: bp.powered_by_wt ?? true,
      isBranded: true,
      brandProfile: bp,
    };
  }, [bp, organization?.name]);

  useEffect(() => {
    const root = document.documentElement;
    const accentHex = bp?.accent_color || bp?.primary_color;

    if (!accentHex) {
      // Remove any previously set overrides
      for (const v of ACCENT_VARS) root.style.removeProperty(v);
      for (const v of FOREGROUND_VARS) root.style.removeProperty(v);
      return;
    }

    const hsl = hexToHslString(accentHex);
    if (!hsl) return;

    // Set accent-color overrides
    for (const v of ACCENT_VARS) {
      root.style.setProperty(v, hsl);
    }

    // Compute foreground: dark text on light accent, light text on dark accent
    const fgHsl = isLightColor(accentHex) ? '222 47% 6%' : '210 40% 98%';
    for (const v of FOREGROUND_VARS) {
      root.style.setProperty(v, fgHsl);
    }

    return () => {
      for (const v of ACCENT_VARS) root.style.removeProperty(v);
      for (const v of FOREGROUND_VARS) root.style.removeProperty(v);
    };
  }, [bp?.accent_color, bp?.primary_color]);

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand(): BrandContextValue {
  return useContext(BrandContext);
}
